import kfetch from '@kareo/kfetch';
import qs from 'qs';

/**
 * Options passed into fetch API - https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/fetch#Parameters
 * @typedef {Object} FetchOptions
 * @property {AbortController.signal} [signal=null] - An AbortSignal object instance; allows you to communicate with a fetch request and abort it if desired via an AbortController.
 * @property {string} [method='GET'] The request method, e.g., GET, POST.
 * @property {(string|Blob|BufferSource|FormData|URLSearchParams)} [body=null] - Any body that you want to add to your request: this can be a Blob, BufferSource, FormData, URLSearchParams, or USVString object. Note that a request using the GET or HEAD method cannot have a body.
 */

const patientBaseApiUrl = `/dashboard-calendar-ui/api/Patient/`;

/**
 * API call to get paitient balance
 * @param {number | number[]} patientId
 * @param {boolean} includePostedPayments
 * @param {FetchOptions} [opts]
 */
function getBalance(patientId, includePostedPayments = false, opts) {
  let fetchUrl;
  if (Array.isArray(patientId)) {
    const patientIdsStr = patientId.join(',');
    fetchUrl = `/payments-ui/api/PatientPayment/patientBalances?patientIds=${patientIdsStr}`;
  } else {
    fetchUrl = `/payments-ui/api/PatientPayment/balance/${patientId}${
      includePostedPayments ? '?includePostedPayments=true' : ''
    }`;
  }

  return kfetch(fetchUrl, opts);
}

/**
 * @function getBatchEligibilitySummaryByPatientIds
 * @param {array} patientIds
 * @param {FetchOptions} [opts]
 * @return {Promise<Object>} - an object with keys of the patientIds and array values that represent the eligbility for policies under a case
 */
function getBatchEligibilityByPatientIds(patientIds, opts = {}) {
  let url = `${patientBaseApiUrl}BatchEligibilitySummary`;

  return kfetch(url, {
    ...opts,
    method: 'POST',
    body: JSON.stringify(patientIds),
  });
}

/**
 * @function getBatchEligibilitySummaryByPatientCaseIds
 * @param {array} patientCaseIds
 * @param {FetchOptions} [opts]
 * @return {Promise<Object>} - an object with keys of the caseIds and array values that represent the eligbility for policies under a case
 */
function getBatchEligibilitySummaryByPatientCaseIds(patientCaseIds, opts = {}) {
  let url = `${patientBaseApiUrl}BatchEligibilitySummaryByPatientCaseIds`;

  return kfetch(url, {
    ...opts,
    method: 'POST',
    body: JSON.stringify(patientCaseIds),
  });
}

/**
 * @function getBatchLastEligibilitySummaryByPatientCaseIdOrPatientId
 * @param {array} patientCaseIds
 * @param {array} patientIds
 * @param {FetchOptions} [opts]
 * @return {Promise<any>} - an object with keys of the caseIds and array values that represent the eligbility for policies under a case
 */
function getBatchLastEligibilitySummaryByPatientCaseIdOrPatientId(
  patientCaseIdAppointments,
  patientIdAppointments,
  opts = {},
) {
  let url = `${patientBaseApiUrl}BatchEligibilitySummaryByPatientCaseIdsOrPatientIds`;

  const patientCaseKeys = Object.keys(patientCaseIdAppointments);
  const patientIdKeys = Object.keys(patientIdAppointments);
  if (!patientCaseKeys.length && !patientIdKeys.length) {
    return Promise.reject(
      new Error(
        'You must have either patientCases or patientIds to get batch eligbility',
      ),
    );
  }

  return kfetch(url, {
    ...opts,
    method: 'POST',
    body: JSON.stringify({
      patientCaseIdAppointments,
      patientIdAppointments,
    }),
  });
}

/**
 * API call to get patient Cases
 * @param {number} patientId
 * @param {FetchOptions} [opts]
 */
function getInsuranceCases(patientId, opts) {
  let url = `/dashboard-calendar-ui/api/Insurance/Patient/${patientId}/Case`;

  return kfetch(url, opts);
}

/**
 * Optional query parameters passed to Eligibility Summary endpoints
 * @typedef {Object} EligibilityQueryParams
 * @property {number} dateOfService - JS timestamp
 * @property {number} InsuranceCompanyId - insurance company id found in Case
 * @property {string}  policyGuid - insurance policy id found in Case
 */

/**
 * API call for to determine patient insurance policy eligibility
 * @function getEligibilitySummary
 * @param {number} patientId
 * @param {EligibilityQueryParams} [queryParams] {@link EligibilityQueryParams}
 * @param {FetchOptions} [opts]
 */
function getEligibilitySummary(patientId, queryParams, opts) {
  let url = `${patientBaseApiUrl}PatientId/${patientId}/EligibilitySummary`;

  if (queryParams) {
    url += '?' + qs.stringify(queryParams);
  }

  return kfetch(url, opts);
}

/**
 *
 * API call to run eligibility check for a patients policy
 * @function runEligibilityCheck
 * @param {number} patientId
 * @param {number} providerId
 * @param {EligibilityQueryParams} [params] {@link EligibilityQueryParams}
 * @param {FetchOptions} [opts]
 */
function runEligibilityCheck(patientId, providerId, params, opts) {
  let url = `${patientBaseApiUrl}PatientId/${patientId}/ProviderId/${providerId}/EligibilitySummary`;

  if (params) {
    opts = Object.assign({}, opts, {
      body: JSON.stringify(params),
    });
  }

  return kfetch(url, { ...opts, method: 'POST' });
}

/**
 *
 * API call to get the Patien Preferred Address
 * @function getPatientPreferredAddress
 * @param {number} patientId
 * @param {FetchOptions} [opts]
 */
const getPatientPreferredAddress = (patientId, opts) => {
  let url = `${patientBaseApiUrl}PreferredAddress/PatientId/${patientId}`;
  return kfetch(url, opts);
};

export default {
  getBalance,
  getBatchEligibilityByPatientIds,
  getBatchEligibilitySummaryByPatientCaseIds,
  getBatchLastEligibilitySummaryByPatientCaseIdOrPatientId,
  getInsuranceCases,
  getEligibilitySummary,
  runEligibilityCheck,
  getPatientPreferredAddress,
};
